import React, { useRef, useCallback, useState, useEffect } from 'react';
import { MousePointer2, BookOpen, ChevronUp, ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import '../../styles/gallery-hud.css';

export function StaticJoystick({ joystickRef, isDetailMode = false }) {
  const MAX      = 38;
  const touchId  = useRef(null);
  const baseRef  = useRef(null);
  const thumbRef = useRef(null);

  // Reset joystick when disabled
  useEffect(() => {
    if (isDetailMode) {
      touchId.current = null;
      if (thumbRef.current) thumbRef.current.style.transform = 'translate(-50%, -50%)';
      joystickRef.current = { x: 0, y: 0 };
    }
    // thumbRef et joystickRef sont des refs stables — pas de re-effet nécessaire
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailMode]);

  const getClampedPos = useCallback((touch) => {
    const rect = baseRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const cx = rect.left + rect.width / 2;
    const cy = rect.top  + rect.height / 2;
    const dx = touch.clientX - cx;
    const dy = touch.clientY - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist === 0) return { x: 0, y: 0 };
    const clamped = Math.min(dist, MAX);
    return { x: (dx / dist) * clamped, y: (dy / dist) * clamped };
  }, []);

  const updateThumb = useCallback((x, y) => {
    if (thumbRef.current) thumbRef.current.style.transform = `translate(calc(-50% + ${x}px), calc(-50% + ${y}px))`;
    joystickRef.current = { x: x / MAX, y: y / MAX };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joystickRef]); // thumbRef et MAX sont stables — inclus dans la closure uniquement

  const onStart = useCallback((e) => {
    if (isDetailMode) return;
    // Signaler l'activité au timer d'inactivité (stopPropagation bloque la remontée)
    window.dispatchEvent(new Event('joystick-activity'));
    e.stopPropagation(); e.preventDefault();
    if (touchId.current !== null) return;
    const t = e.changedTouches[0];
    touchId.current = t.identifier;
    updateThumb(...Object.values(getClampedPos(t)));
  }, [getClampedPos, updateThumb, isDetailMode]);

  const onMove = useCallback((e) => {
    // Signaler l'activité au timer d'inactivité
    window.dispatchEvent(new Event('joystick-activity'));
    e.stopPropagation();
    if (touchId.current === null) return;
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (t.identifier !== touchId.current) continue;
      const pos = getClampedPos(t);
      updateThumb(pos.x, pos.y);
      break;
    }
  }, [getClampedPos, updateThumb]);

  const onEnd = useCallback((e) => {
    e.stopPropagation();
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier !== touchId.current) continue;
      touchId.current = null;
      updateThumb(0, 0);
      break;
    }
  }, [updateThumb]);

  return (
    <div className="static-joystick-container" ref={baseRef}
      onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} onTouchCancel={onEnd}
      data-testid="static-joystick"
    >
      <div className="static-joystick-base">
        <div className="static-joystick-guide" />
        <div className="static-joystick-cardinal static-joystick-cardinal-n" />
        <div className="static-joystick-cardinal static-joystick-cardinal-s" />
        <div className="static-joystick-cardinal static-joystick-cardinal-w" />
        <div className="static-joystick-cardinal static-joystick-cardinal-e" />
        <div ref={thumbRef} className="static-joystick-thumb" />
      </div>
    </div>
  );
}

export function MobileLookZone({ lookRef, isDetailMode, active = true }) {
  const touchId = useRef(null);
  const lastX   = useRef(0);
  const tapRef  = useRef({ startTime: 0, startX: 0, startY: 0 });

  const onStart = useCallback((e) => {
    if (!active || isDetailMode || touchId.current !== null) return;
    window.dispatchEvent(new Event('joystick-activity'));
    const t = e.changedTouches[0];
    touchId.current = t.identifier;
    lastX.current   = t.clientX;
    tapRef.current  = { startTime: Date.now(), startX: t.clientX, startY: t.clientY };
  }, [isDetailMode, active]);

  const onMove = useCallback((e) => {
    if (!active || isDetailMode) return;
    window.dispatchEvent(new Event('joystick-activity'));
    for (let i = 0; i < e.changedTouches.length; i++) {
      if (e.changedTouches[i].identifier === touchId.current) {
        lookRef.current.dx = (e.changedTouches[i].clientX - lastX.current) * 0.004;
        lastX.current = e.changedTouches[i].clientX;
        break;
      }
    }
  }, [isDetailMode, lookRef, active]);

  const onEnd = useCallback((e) => {
    for (let i = 0; i < e.changedTouches.length; i++) {
      const t = e.changedTouches[i];
      if (t.identifier !== touchId.current) continue;
      touchId.current = null;
      lookRef.current.dx = 0;
      if (!active) break;
      const duration = Date.now() - tapRef.current.startTime;
      const dx = t.clientX - tapRef.current.startX;
      const dy = t.clientY - tapRef.current.startY;
      if (duration < 280 && Math.sqrt(dx * dx + dy * dy) < 14) {
        const canvas = document.querySelector('canvas');
        if (canvas) {
          const evOpts = { bubbles: true, cancelable: true, clientX: t.clientX, clientY: t.clientY, pointerId: 1, pointerType: 'touch', isPrimary: true, view: window };
          canvas.dispatchEvent(new PointerEvent('pointerdown', evOpts));
          canvas.dispatchEvent(new PointerEvent('pointerup', evOpts));
          canvas.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, clientX: t.clientX, clientY: t.clientY, view: window }));
        }
      }
      break;
    }
  }, [lookRef, active]);

  return <div
    className="look-zone"
    data-testid="mobile-look-zone"
    style={active ? undefined : { pointerEvents: 'none' }}
    onTouchStart={onStart} onTouchMove={onMove} onTouchEnd={onEnd} onTouchCancel={onEnd}
  />;
}

export function GalleryHUD({ isDetailMode, isMobile, onReturnToStart, language, isNearGuestbook, onOpenGuestbook, nearArtworkId }) {
  const isFr = language === 'fr';
  const isHe = language === 'he';
  const t = (fr, he, en) => isHe ? he : isFr ? fr : en;

  // Oeuvres horizontales — indicateur légèrement plus haut sur desktop
  const HORIZONTAL_ARTWORK_IDS = ['sous-la-presence'];
  const isNearHorizontal = HORIZONTAL_ARTWORK_IDS.includes(nearArtworkId);

  // Joystick hint — visible 3s puis disparaît
  const [showJoystickHint, setShowJoystickHint] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowJoystickHint(false), 3200);
    return () => clearTimeout(t);
  }, []);

  const handleGuestbookTap = useCallback(() => {
    onOpenGuestbook?.();
  }, [onOpenGuestbook]);

  if (isDetailMode) return null;

  return (
    <div className="gallery-hud" data-testid="gallery-hud">

      {/* ── Indicateur de proximité — le texte est dans le cadre 3D (Html drei) ──
          Le HUD ne montre plus rien quand l'avatar est près d'une oeuvre.
          La ligne or + "CLIQUER POUR DÉCOUVRIR" sont ancrés dans ArtworkFrame.jsx ── */}

      {/* ── Indicateur guestbook ── */}
      {isNearGuestbook && (
        <button
          className="guestbook-tap-indicator"
          onClick={handleGuestbookTap}
          onTouchStart={(e) => { e.stopPropagation(); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); handleGuestbookTap(); }}
          data-testid="guestbook-proximity-button"
          aria-label={t("Livre d'Or", 'ספר אורחים', 'Guest Book')}
        >
          <div className="gbk-ripple gbk-ripple-1" />
          <div className="gbk-ripple gbk-ripple-2" />
          <div className="gbk-icon">
            <BookOpen size={20} strokeWidth={1.5} />
          </div>
          <span className="gbk-label">{t("Livre d'Or", 'ספר אורחים', 'Guest Book')}</span>
        </button>
      )}

      {/* ── Indice de déplacement général ── */}
      {!nearArtworkId && !isNearGuestbook && (
        <div className="gallery-move-hint" data-testid="gallery-move-hint">
          {isMobile
            ? t('Joystick pour avancer · Glisser pour regarder', "ג'ויסטיק להזזה · החלק להסתכל", 'Joystick to move · Swipe to look')
            : t('WASD ou Flèches pour naviguer', 'WASD או חצים לניווט', 'WASD or Arrows to navigate')
          }
        </div>
      )}

      {/* ── Indice joystick 3s au démarrage (mobile) ── */}
      {isMobile && showJoystickHint && (
        <div className="joystick-hint-arrow" data-testid="joystick-hint">
          <div className="joystick-hint-pulse" />
          <span className="joystick-hint-label">
            {t('Déplacer', 'זוז', 'Move')}
          </span>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   Touches clavier visuelles (Desktop uniquement)
   Transparentes, bordure or chaud, cliquables
───────────────────────────────────────────── */
export function DesktopKeyboardHint({ isDetailMode }) {
  const [pressed, setPressed] = useState({});

  const press = useCallback((code) => {
    setPressed(p => ({ ...p, [code]: true }));
    window.dispatchEvent(new KeyboardEvent('keydown', { code, bubbles: true }));
  }, []);

  const release = useCallback((code) => {
    setPressed(p => ({ ...p, [code]: false }));
    window.dispatchEvent(new KeyboardEvent('keyup', { code, bubbles: true }));
  }, []);

  if (isDetailMode) return null;

  const Key = ({ code, icon: Icon, label, extraClass = '' }) => (
    <button
      className={`dk-key ${pressed[code] ? 'dk-active' : ''} ${extraClass}`}
      data-testid={`desktop-key-${code.replace('Arrow', '').toLowerCase()}`}
      onMouseDown={() => press(code)}
      onMouseUp={() => release(code)}
      onMouseLeave={() => release(code)}
      onTouchStart={(e) => { e.preventDefault(); press(code); }}
      onTouchEnd={() => release(code)}
      aria-label={label}
    >
      <Icon size={15} strokeWidth={1.8} />
    </button>
  );

  return (
    <div className="desktop-keys-hint" data-testid="desktop-keyboard-hint">
      <div className="dk-row dk-row-top">
        <Key code="ArrowUp"    icon={ChevronUp}    label="Avancer" />
      </div>
      <div className="dk-row dk-row-bottom">
        <Key code="ArrowLeft"  icon={ChevronLeft}  label="Gauche" />
        <Key code="ArrowDown"  icon={ChevronDown}  label="Reculer" />
        <Key code="ArrowRight" icon={ChevronRight} label="Droite" />
      </div>
    </div>
  );
}

